import React from 'react';
import { motion } from 'framer-motion';
import { BedDoubleIcon, CreditCardIcon, TrendingUpIcon, ArrowRightIcon, RefreshCwIcon, ShieldCheckIcon, UsersIcon, CalendarCheckIcon, GraduationCapIcon, RouteIcon, SparklesIcon, CheckIcon } from 'lucide-react';
import { Header } from '../Header';
import { SiteFooter } from '../SiteFooter';
import { FadeIn } from '../ui';
export function CloudbedsPage() {
  return <div className="min-h-screen bg-white font-sans text-slate-800 flex flex-col" data-id="element-4531">
      <Header data-id="element-4532" />

      <main className="flex-1" data-id="element-4533">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden bg-[#0A0F1F]" data-id="element-4534">
          {/* Ambient background */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-4535">
            <motion.div animate={{
            x: [0, 30, 0],
            y: [0, -20, 0]
          }} transition={{
            duration: 14,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-brand-coral/[0.13] rounded-full blur-[100px]" data-id="element-4536" />
            <motion.div animate={{
            x: [0, -25, 0],
            y: [0, 30, 0]
          }} transition={{
            duration: 16,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute top-1/4 -right-40 w-[500px] h-[500px] bg-brand-teal/[0.15] rounded-full blur-[100px]" data-id="element-4537" />
          </div>

          {/* Grid texture */}
          <div className="absolute inset-0 opacity-[0.15] pointer-events-none" style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)'
        }} data-id="element-4538" />

          <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10 text-center" data-id="element-4539">
            <FadeIn data-id="element-4540">
              <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full pl-2 pr-4 py-1.5 mb-8 backdrop-blur-sm" data-id="element-4541">
                <span className="bg-brand-teal text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-4542">
                  Integration
                </span>
                <span className="text-sm font-medium text-slate-300" data-id="element-4543">
                  Offsite+ &amp; Cloudbeds
                </span>
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-4544">
              <h1 className="text-5xl md:text-7xl font-bold leading-[1.1] tracking-tight text-white mb-6 text-balance" data-id="element-4545">
                Turn group business into{' '}
                <span className="text-brand-teal" data-id="element-4546">revenue</span> — straight from
                your PMS.
              </h1>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-4547">
              <p className="text-lg md:text-xl text-slate-400 mb-10 leading-relaxed text-balance max-w-3xl mx-auto" data-id="element-4548">
                Offsite Plus connects directly to Cloudbeds — syncing room
                blocks, reservations, rates, and folio charges in real time.
                Your team designs unforgettable corporate offsites while every
                room night and ancillary dollar flows back into the system you
                already run on.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} data-id="element-4549">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16" data-id="element-4550">
                <a href="https://calendly.com/ashsingh/post-hitec-executive-briefing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-center leading-none whitespace-nowrap rounded-full font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed no-underline bg-brand-teal text-white hover:bg-teal-500 focus:ring-brand-teal shadow-lg shadow-brand-teal/20 text-base px-8 py-4 group w-full sm:w-auto" data-id="element-4551">
                  Book an Integration Briefing
                  <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-4552" />
                </a>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* Value Props */}
        <section className="py-24 bg-white" data-id="element-4553">
          <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-4554">
            <FadeIn data-id="element-4555">
              <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-4556">
                <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-4 tracking-tight" data-id="element-4557">
                  Your PMS and your experience layer, finally in sync.
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed" data-id="element-4558">
                  Cloudbeds runs your reservations, rates, and payments.
                  Offsite+ designs the programming and captures the upside —
                  with no double entry.
                </p>
              </div>
            </FadeIn>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-4559">
              <FadeIn delay={0.1} data-id="element-4560">
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 h-full hover:shadow-md transition-shadow" data-id="element-4561">
                  <div className="w-14 h-14 bg-brand-navy/10 rounded-2xl flex items-center justify-center mb-6" data-id="element-4562">
                    <BedDoubleIcon className="w-7 h-7 text-brand-navy" data-id="element-4563" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-4564">
                    Effortless Group Blocks
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-4565">
                    Create and manage room blocks for corporate offsites that
                    sync instantly with Cloudbeds availability and rates — no
                    spreadsheets, no manual rooming lists.
                  </p>
                </div>
              </FadeIn>

              <FadeIn delay={0.2} data-id="element-4566">
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 h-full hover:shadow-md transition-shadow" data-id="element-4567">
                  <div className="w-14 h-14 bg-brand-coral/10 rounded-2xl flex items-center justify-center mb-6" data-id="element-4568">
                    <CreditCardIcon className="w-7 h-7 text-brand-coral" data-id="element-4569" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-4570">
                    Ancillary Revenue Capture
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-4571">
                    F&amp;B, spa, wellness, and activity bookings prompted by
                    Offsite+ post directly to the guest folio in Cloudbeds —
                    capturing spend most properties never see.
                  </p>
                </div>
              </FadeIn>

              <FadeIn delay={0.3} data-id="element-4572">
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 h-full hover:shadow-md transition-shadow" data-id="element-4573">
                  <div className="w-14 h-14 bg-brand-teal/10 rounded-2xl flex items-center justify-center mb-6" data-id="element-4574">
                    <TrendingUpIcon className="w-7 h-7 text-brand-teal" data-id="element-4575" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-4576">
                    Smarter Revenue Decisions
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-4577">
                    Combine Cloudbeds reservation data with Offsite+ engagement
                    and outcome metrics to defend premium group pricing and win
                    repeat corporate bookings.
                  </p>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* Technical Integration */}
        <section className="py-24 bg-slate-50 border-t border-slate-100" data-id="element-4578">
          <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-4579">
            <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-4580">
              <FadeIn data-id="element-4581">
                <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-6 tracking-tight" data-id="element-4582">
                  A two-way sync built on the Cloudbeds API.
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-4583">
                  The integration keeps a single source of truth: reservations,
                  rates, and guest profiles live in Cloudbeds, while Offsite+
                  layers on the programming and pushes new revenue back —
                  automatically.
                </p>

                <ul className="space-y-6" data-id="element-4584">
                  {[{
                  icon: RefreshCwIcon,
                  title: 'Real-Time Two-Way Sync',
                  desc: 'Reservations, room blocks, and folio charges stay in lockstep across both platforms.'
                }, {
                  icon: UsersIcon,
                  title: 'Unified Guest Profiles',
                  desc: 'Match Offsite+ attendees to Cloudbeds guest records for personalized programming and clean reporting.'
                }, {
                  icon: ShieldCheckIcon,
                  title: 'Secure & Compliant',
                  desc: 'OAuth-based connection with bank-grade encryption protects guest and payment data end to end.'
                }].map((item, i) => <li key={i} className="flex gap-4" data-id="element-4585">
                      <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-4586">
                        <item.icon className="w-5 h-5 text-brand-teal" data-id="element-4587" />
                      </div>
                      <div data-id="element-4588">
                        <h4 className="text-base font-bold text-brand-navy mb-1" data-id="element-4589">
                          {item.title}
                        </h4>
                        <p className="text-sm text-slate-600 leading-relaxed" data-id="element-4590">
                          {item.desc}
                        </p>
                      </div>
                    </li>)}
                </ul>
              </FadeIn>

              <FadeIn delay={0.2} data-id="element-4591">
                <div className="relative" data-id="element-4592">
                  <div className="absolute inset-0 bg-gradient-to-tr from-brand-teal/20 to-brand-coral/20 rounded-3xl blur-3xl transform rotate-3" data-id="element-4593" />
                  <div className="relative bg-white border border-slate-200 rounded-3xl p-8 shadow-xl" data-id="element-4594">
                    <div className="flex items-center justify-between mb-8 pb-6 border-b border-slate-100" data-id="element-4595">
                      <div className="font-bold text-xl text-brand-navy" data-id="element-4596">
                        Cloudbeds PMS
                      </div>
                      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-brand-teal bg-brand-teal/10 px-2 py-1 rounded-full" data-id="element-4597">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-teal animate-pulse" data-id="element-4598" />
                        Synced
                      </div>
                    </div>

                    <div className="space-y-4" data-id="element-4599">
                      <div className="h-20 bg-slate-50 rounded-xl border border-slate-100 flex items-center px-6 gap-4" data-id="element-4600">
                        <div className="w-11 h-11 rounded-full bg-brand-navy/10 flex items-center justify-center shrink-0" data-id="element-4601">
                          <CalendarCheckIcon className="w-5 h-5 text-brand-navy" data-id="element-4602" />
                        </div>
                        <div className="flex-1" data-id="element-4603">
                          <div className="text-sm font-bold text-brand-navy mb-1" data-id="element-4604">
                            Acme Corp · Leadership Offsite
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-4605">
                            48 room nights · Jun 15–18
                          </div>
                        </div>
                      </div>

                      {/* Offsite+ Module Simulation */}
                      <div className="p-6 bg-brand-navy rounded-xl text-white relative overflow-hidden" data-id="element-4606">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-brand-coral/20 rounded-full blur-2xl transform translate-x-1/2 -translate-y-1/2" data-id="element-4607" />
                        <div className="flex items-center gap-2 mb-4" data-id="element-4608">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-brand-coral" data-id="element-4609">
                            Pushed by Offsite+
                          </span>
                        </div>
                        <div className="text-lg font-bold mb-2" data-id="element-4610">
                          +$6,200 ancillary spend
                        </div>
                        <div className="text-sm text-slate-300 mb-4" data-id="element-4611">
                          Spa, dine-around &amp; activities posted to folio.
                        </div>
                        <div className="h-10 bg-white/10 rounded-lg flex items-center px-4 backdrop-blur-sm border border-white/5" data-id="element-4612">
                          <span className="text-sm font-medium" data-id="element-4613">
                            View in Cloudbeds
                          </span>
                          <ArrowRightIcon className="w-4 h-4 ml-auto" data-id="element-4614" />
                        </div>
                      </div>

                      <div className="h-20 bg-slate-50 rounded-xl border border-slate-100 flex items-center px-6 gap-4" data-id="element-4615">
                        <div className="w-11 h-11 rounded-full bg-brand-coral/10 flex items-center justify-center shrink-0" data-id="element-4616">
                          <CreditCardIcon className="w-5 h-5 text-brand-coral" data-id="element-4617" />
                        </div>
                        <div className="flex-1" data-id="element-4618">
                          <div className="text-sm font-bold text-brand-navy mb-1" data-id="element-4619">
                            Folio updated
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-4620">
                            Charges reconciled automatically
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* Where we draw the line */}
        <section className="py-24 bg-white border-t border-slate-100" data-id="element-4621">
          <div className="max-w-6xl mx-auto px-6 md:px-12" data-id="element-4622">
            <FadeIn data-id="element-4623">
              <div className="max-w-3xl mx-auto text-center mb-14" data-id="element-4624">
                <div className="inline-flex items-center gap-2 bg-brand-navy/5 border border-brand-navy/10 rounded-full px-3 py-1 mb-6" data-id="element-4625">
                  <span className="text-xs font-bold text-brand-navy uppercase tracking-wider" data-id="element-4626">
                    Complementary, not competing
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-4 tracking-tight" data-id="element-4627">
                  We don't replace your PMS — we sit on top of it.
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed" data-id="element-4628">
                  Cloudbeds remains your system of record for reservations,
                  rates, and payments. Offsite Plus adds the one thing a PMS was
                  never built to do — customized learning pathways. There is
                  zero overlap.
                </p>
              </div>
            </FadeIn>

            <div className="grid md:grid-cols-2 gap-6 mb-16" data-id="element-4629">
              <FadeIn delay={0.1} data-id="element-4630">
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 h-full" data-id="element-4631">
                  <div className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-5" data-id="element-4632">
                    Cloudbeds provides
                  </div>
                  <ul className="space-y-3" data-id="element-4633">
                    {['Reservations, room blocks & availability', 'Rates, payments & the guest folio', 'Channel management & distribution', 'Guest records & system of record'].map(t => <li key={t} className="flex items-start gap-3" data-id="element-4634">
                        <CheckIcon className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" data-id="element-4635" />
                        <span className="text-slate-600" data-id="element-4636">{t}</span>
                      </li>)}
                  </ul>
                </div>
              </FadeIn>

              <FadeIn delay={0.2} data-id="element-4637">
                <div className="bg-brand-navy rounded-2xl p-8 h-full text-white relative overflow-hidden" data-id="element-4638">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-brand-teal/20 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3" data-id="element-4639" />
                  <div className="relative" data-id="element-4640">
                    <div className="text-sm font-bold text-brand-teal uppercase tracking-wider mb-5" data-id="element-4641">
                      Offsite Plus provides
                    </div>
                    <ul className="space-y-3" data-id="element-4642">
                      {['AI-built customized learning pathways per group', 'Personalized session & content sequencing', 'Group programming that fills the room block', 'Measurable outcomes & ancillary revenue lift'].map(t => <li key={t} className="flex items-start gap-3" data-id="element-4643">
                          <CheckIcon className="w-5 h-5 text-brand-teal shrink-0 mt-0.5" data-id="element-4644" />
                          <span className="text-slate-200" data-id="element-4645">{t}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </FadeIn>
            </div>

            {/* Customized learning pathways — our lane */}
            <FadeIn delay={0.15} data-id="element-4646">
              <div className="bg-slate-50 rounded-3xl border border-slate-100 p-8 md:p-12" data-id="element-4647">
                <div className="max-w-3xl mb-12" data-id="element-4648">
                  <div className="inline-flex items-center gap-2 bg-brand-coral/10 rounded-full px-3 py-1 mb-5" data-id="element-4649">
                    <SparklesIcon className="w-3.5 h-3.5 text-brand-coral" data-id="element-4650" />
                    <span className="text-xs font-bold text-brand-coral uppercase tracking-wider" data-id="element-4651">
                      Our clear pathway
                    </span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-brand-navy mb-4 tracking-tight" data-id="element-4652">
                    Customized learning pathways are what we do.
                  </h3>
                  <p className="text-lg text-slate-600 leading-relaxed" data-id="element-4653">
                    Cloudbeds books the rooms. We make sure those rooms fill
                    with a program worth paying a premium for. For every
                    corporate group, our AI assembles a unique learning pathway
                    from each attendee's role, goals, and the client's
                    objectives — then sequences sessions, content, and
                    reflection prompts so the offsite delivers measurable
                    results. The ancillary spend it generates flows straight
                    back into the Cloudbeds folio.
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6" data-id="element-4654">
                  {[{
                  icon: RouteIcon,
                  title: 'Built per group, per person',
                  desc: 'Each attendee gets a pathway mapped to their role, seniority, and the offsite goals — no two are identical.'
                }, {
                  icon: GraduationCapIcon,
                  title: 'Sequenced for retention',
                  desc: 'Sessions, micro-content, and reflection prompts are ordered so learning compounds across the days.'
                }, {
                  icon: TrendingUpIcon,
                  title: 'Drives folio revenue',
                  desc: 'Pathway moments trigger spa, dining, and activity upsells that post directly to Cloudbeds.'
                }].map((item, i) => <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100" data-id="element-4655">
                      <div className="w-11 h-11 rounded-xl bg-brand-teal/10 flex items-center justify-center mb-4" data-id="element-4656">
                        <item.icon className="w-5 h-5 text-brand-teal" data-id="element-4657" />
                      </div>
                      <h4 className="text-base font-bold text-brand-navy mb-2" data-id="element-4658">
                        {item.title}
                      </h4>
                      <p className="text-sm text-slate-600 leading-relaxed" data-id="element-4659">
                        {item.desc}
                      </p>
                    </div>)}
                </div>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-white text-center" data-id="element-4660">
          <div className="max-w-3xl mx-auto px-6 md:px-12" data-id="element-4661">
            <FadeIn data-id="element-4662">
              <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-6" data-id="element-4663">
                Ready to turn offsites into measurable revenue?
              </h2>
              <p className="text-lg text-slate-600 mb-10" data-id="element-4664">
                Let's walk through how the Offsite+ and Cloudbeds integration
                drives more group bookings and ancillary spend for your property
                — without changing the system your team already loves.
              </p>
              <a href="https://calendly.com/ashsingh/post-hitec-executive-briefing" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-center leading-none whitespace-nowrap rounded-full font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 no-underline bg-brand-navy text-white hover:bg-slate-800 focus:ring-brand-navy shadow-lg text-base px-8 py-4 group" data-id="element-4665">
                Schedule a Demo
                <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-4666" />
              </a>
            </FadeIn>
          </div>
        </section>
      </main>

      <SiteFooter data-id="element-4667" />
    </div>;
}